import React, { useMemo, useState, useCallback } from 'react';
import { Rnd } from 'react-rnd';

import type { OCRData, FHIRResource, BBoxMap } from './types';
import { getRandomColor } from './utils/color';

type Props = {
  bboxData: FHIRResource[] | null;
  ocrData: OCRData | null;
  currentPage: number;
  originalWidth: number;
  originalHeight: number;
  setBboxData: React.Dispatch<React.SetStateAction<FHIRResource[] | null>>;
  canvasRect: { width: number; height: number } | null;
  zoom: number; // <-- IMPORTANT
};

type FlattenedBox = {
  key: string;
  label: string;
  bbox: [number, number, number, number];
};

type BBoxOverrides = Record<string, [number, number, number, number]>;

const BBoxOverlay: React.FC<Props> = ({
  bboxData,
  ocrData,
  currentPage,
  originalWidth,
  originalHeight,
  setBboxData,
  canvasRect,
  zoom,
}) => {
  const [bboxOverrides, setBboxOverrides] = useState<BBoxOverrides>({});

  const pageKey = `Page_${currentPage}`;
  const ocrPage = ocrData?.[pageKey] ?? null;

  //
  // GEOMETRY IN UNZOOMED LAYOUT COORDINATES
  //
  const baseWidth = ocrPage?.width || originalWidth || 0;
  const baseHeight = ocrPage?.height || originalHeight || 0;

  const displayWidth = canvasRect?.width ?? 0;
  const displayHeight = canvasRect?.height ?? 0;

  const hasGeometry =
    !!ocrPage &&
    baseWidth > 0 &&
    baseHeight > 0 &&
    displayWidth > 0 &&
    displayHeight > 0;

  const scaleX = hasGeometry ? displayWidth / baseWidth : 1;
  const scaleY = hasGeometry ? displayHeight / baseHeight : 1;

  /**
   * Flatten bbox data for this page
   */
  const flattenedBoxes = useMemo<FlattenedBox[]>(() => {
    if (!bboxData || !hasGeometry) return [];

    const list: FlattenedBox[] = [];

    bboxData.forEach((resource, resourceIdx) => {
      resource.Resource_data.forEach((item, dataIdx) => {
        item.bboxes
          .filter((b) => String(b.page) === String(currentPage))
          .forEach((b, bboxIdx) => {
            const key = `${resourceIdx}-${dataIdx}-${bboxIdx}`;
            const override =
              key in bboxOverrides ? bboxOverrides[key] : undefined;

            const bbox = override ?? b.bbox;

            list.push({ key, label: item.label, bbox });
          });
      });
    });

    return list;
  }, [bboxData, bboxOverrides, currentPage, hasGeometry]);

  /**
   * Extract text using OCR geometry - word-level precision
   * Only includes words that actually fall within the bounding box
   */
  const extractText = useCallback(
    (
      bbox: [number, number, number, number],
      fallbackText: string | null | undefined
    ) => {
      if (!ocrPage) return fallbackText ?? '';

      const [bx1, by1, bx2, by2] = bbox;

      // Collect words from overlapping OCR items
      const extractedWords: Array<{ text: string; x: number; y: number }> = [];

      ocrPage.OCR.forEach((ocrItem) => {
        const [ox1, oy1, ox2, oy2] = ocrItem.bbox;

        const itemHeight = oy2 - oy1;

        // Check vertical overlap first (line must be within bbox vertically)
        const overlapY = Math.max(0, Math.min(by2, oy2) - Math.max(by1, oy1));
        const vertPct = overlapY / itemHeight;

        // Skip if no significant vertical overlap
        if (vertPct < 0.3) return;

        // Split OCR text into words and estimate each word's position
        const words = ocrItem.text.split(/\s+/).filter((w) => w.length > 0);
        if (words.length === 0) return;

        const ocrWidth = ox2 - ox1;
        const totalCharCount = words.reduce((sum, w) => sum + w.length, 0);
        const spaceWidth =
          words.length > 1
            ? (ocrWidth * 0.05) / (words.length - 1) // estimate space width
            : 0;

        let currentX = ox1;

        words.forEach((word) => {
          // Estimate word width proportionally based on character count
          const wordWidthRatio = word.length / totalCharCount;
          const wordWidth =
            (ocrWidth - spaceWidth * (words.length - 1)) * wordWidthRatio;

          const wordX1 = currentX;
          const wordX2 = currentX + wordWidth;

          // Check if word's horizontal span overlaps with the bbox
          const overlapX = Math.max(
            0,
            Math.min(bx2, wordX2) - Math.max(bx1, wordX1)
          );
          const horizPct = overlapX / wordWidth;

          // Include word if at least 50% of it is within the bbox horizontally
          if (horizPct >= 0.5) {
            extractedWords.push({
              text: word,
              x: wordX1,
              y: oy1,
            });
          }

          currentX = wordX2 + spaceWidth;
        });
      });

      if (extractedWords.length === 0) return fallbackText ?? '';

      // Sort by reading order (top to bottom, left to right)
      extractedWords.sort((a, b) => {
        const dy = a.y - b.y;
        if (Math.abs(dy) > 5) return dy;
        return a.x - b.x;
      });

      return extractedWords
        .map((w) => w.text)
        .join(' ')
        .trim();
    },
    [ocrPage]
  );

  /**
   * Commit updated bbox -> convert from layout coords -> OCR coords
   * Note: react-rnd's scale prop handles the zoom transform, so rawX/Y/W/H
   * are already in unzoomed layout coordinates.
   */
  const commitBox = useCallback(
    (
      key: string,
      label: string,
      rawX: number, // layout coordinates (unzoomed)
      rawY: number,
      rawW: number,
      rawH: number
    ) => {
      if (!hasGeometry) return;

      // rawX/Y/W/H are already in layout-space thanks to react-rnd's scale prop
      const displayX = rawX;
      const displayY = rawY;
      const displayW = rawW;
      const displayH = rawH;

      // Convert layout-space → OCR-space
      const newX1 = displayX / scaleX;
      const newY1 = displayY / scaleY;
      const newX2 = (displayX + displayW) / scaleX;
      const newY2 = (displayY + displayH) / scaleY;

      const newBBox: [number, number, number, number] = [
        newX1,
        newY1,
        newX2,
        newY2,
      ];

      setBboxOverrides((prev) => ({ ...prev, [key]: newBBox }));

      const hydratedText = extractText(newBBox, '');

      // Push into form state
      setBboxData((prev) => {
        if (!prev) return prev;

        return prev.map((resource) => {
          const prevMap = (resource._bboxMap ?? {}) as BBoxMap;

          return {
            ...resource,
            Resource_data: resource.Resource_data.map((item) =>
              item.label === label
                ? {
                    ...item,
                    text: hydratedText,
                    bboxes: item.bboxes.map((b) =>
                      String(b.page) === String(currentPage)
                        ? { ...b, bbox: newBBox }
                        : b
                    ),
                  }
                : item
            ),
            _bboxMap: {
              ...prevMap,
              [label]: [
                {
                  text: hydratedText,
                  bbox: newBBox,
                  page: String(currentPage),
                },
              ],
            },
          };
        });
      });
    },
    [extractText, hasGeometry, scaleX, scaleY, setBboxData, currentPage]
  );

  if (!hasGeometry) return null;

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
      }}
    >
      {flattenedBoxes.map(({ key, label, bbox }) => {
        const [x1, y1, x2, y2] = bbox;

        const w = (x2 - x1) * scaleX;
        const h = (y2 - y1) * scaleY;

        const displayX = x1 * scaleX;
        const displayY = y1 * scaleY;

        const color = getRandomColor(label);

        return (
          <Rnd
            key={key}
            bounds="parent"
            scale={zoom}
            size={{ width: w, height: h }}
            position={{ x: displayX, y: displayY }}
            enableResizing={{
              top: true,
              right: true,
              bottom: true,
              left: true,
              topRight: true,
              bottomRight: true,
              bottomLeft: true,
              topLeft: true,
            }}
            onDragStop={(_e, d) => commitBox(key, label, d.x, d.y, w, h)}
            onResizeStop={(_e, _dir, ref, _delta, pos) =>
              commitBox(
                key,
                label,
                pos.x,
                pos.y,
                ref.offsetWidth,
                ref.offsetHeight
              )
            }
            style={{
              position: 'absolute',
              border: `2px solid ${color}`,
              boxSizing: 'border-box',
              cursor: 'move',
              pointerEvents: 'auto',
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: -14,
                left: 0,
                background: 'white',
                fontSize: 10,
                color,
                padding: '0 2px',
                pointerEvents: 'none',
              }}
            >
              {label}
            </div>
          </Rnd>
        );
      })}
    </div>
  );
};

export default BBoxOverlay;
